import type { Dispatch, SetStateAction } from 'react'

import type { ColumnDef, ExpandedState, Table } from '@tanstack/react-table'
import {
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'

export type FileEntry<T> = T & {
  _type: 'file'
  _order: () => number
  _withOrder: (order: number) => FileEntry<T>
  _parent: () => FolderEntry<T> | null
  _id: () => number
}

export type FileRemap<T> = (current: FileEntry<T>[]) => FileEntry<T>[]

export type FolderEntry<T> = T & {
  _type: 'folder'
  _order: () => number
  _withOrder: (order: number) => FolderEntry<T>
  _listChildren: () => FileEntry<T>[]
  _withChildren: (remap: FileRemap<T>) => FolderEntry<T>
  _id: () => number
}

export type Entry<T> = FileEntry<T> | FolderEntry<T>

export interface UseTreeTableProps<Node> {
  data: Entry<Node>[]
  columns: ColumnDef<Entry<Node>>[]
  expanded?: ExpandedState
  onExpandedChange?: Dispatch<SetStateAction<ExpandedState>>
  enableSorting?: boolean
}

export function useTreeTable<Node>(
  props: UseTreeTableProps<Node>,
): Table<Entry<Node>> {
  const { data, columns, enableSorting, expanded, onExpandedChange } = props

  return useReactTable<Entry<Node>>({
    data,
    columns,
    state: { expanded },
    getSubRows: (row) =>
      row._type === 'folder' ? (row._listChildren?.() ?? []) : [],
    onExpandedChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    filterFromLeafRows: true,
    enableSorting,
  })
}
